<script setup lang="ts">
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import dayjs from 'dayjs';

import bankTransferLottieData from '@/assets/lottie/bank-transfer.json';
import { useAccount } from '@/composables/useAccount';
import { useErrorModal } from '@/composables/useErrorModal';
import { type OtcData, useOtc } from '@/composables/useOtc';
import { useTransactions } from '@/composables/useTransactions';
import { add, greaterThan, isZero, sub } from '@/helpers/bigmath';
import { formatAmount, formatIBAN, formatTokenAmount, formatTokenRate } from '@/helpers/formatters';
import { max } from '@/helpers/utils';
import { addSentryBreadcrumb } from '@/logs/sentry';
import { APIError } from '@/references/axios/APIError';
import {
  type BankTransferRecipient,
  BankTransferRecipientType
} from '@/references/axios/bankTransfer/types';
import { AmountMode, type Fees } from '@/references/axios/transactions/types';
import { MAX_TOKEN_VISIBLE_DECIMALS, MIN_SEND_AMOUNT_EUR } from '@/references/constants';
import { CurrencyCode } from '@/references/currency';
import { getNetwork } from '@/references/network';
import { RouteNames } from '@/router';

import FeeInfo from '@/components/FeeInfo.vue';
import FeeModal from '@/components/FeeModal.vue';
import TransactionReview from '@/components/TransactionReview.vue';
import type { UiListPairGroup } from '@/components/ui/UiListPair.types';

interface Props {
  otcData: OtcData;
  fees: Fees;
  bankTransferData: BankTransferRecipient;
  reference?: string;
}

const props = defineProps<Props>();

const state = reactive<{
  otcData: OtcData;
  fees: Fees;
  isLoading: boolean;
  isFeeModalOpened: boolean;
}>({
  otcData: props.otcData,
  fees: props.fees,
  isLoading: false,
  isFeeModalOpened: false
});

const { t } = useI18n();
const { replace } = useRouter();
const { accountData } = useAccount();

let loadingTargetAddressAbortController = new AbortController();

const formattedRate = computed((): string => {
  return formatTokenRate(
    state.otcData.rate,
    Math.min(state.otcData.fromToken.decimals, MAX_TOKEN_VISIBLE_DECIMALS),
    state.otcData.fromToken.symbol,
    CurrencyCode.EUR
  );
});

const aggregateFees = computed((): string => {
  return add(add(state.fees.networkFee, state.fees.transactionFee), state.fees.transferFee);
});

const amountWithoutFee = computed(() => {
  return max(sub(state.otcData.toAmount, aggregateFees.value), '0');
});

const formattedAmount = computed((): string => {
  return formatAmount(amountWithoutFee.value, 2, CurrencyCode.EUR, AmountMode.SymbolFirst);
});

const formattedTotal = computed((): string => {
  return formatTokenAmount(
    state.otcData.fromAmount,
    Math.min(state.otcData.fromToken.decimals, MAX_TOKEN_VISIBLE_DECIMALS),
    state.otcData.fromToken.symbol,
    AmountMode.ValueOnly
  );
});

const beneficiaryName = computed((): string => {
  if (props.bankTransferData.type === BankTransferRecipientType.Business) {
    return props.bankTransferData.firstName.toUpperCase();
  }

  return `${props.bankTransferData.firstName} ${props.bankTransferData.lastName}`.toUpperCase();
});

const details = computed((): UiListPairGroup[] => [
  {
    items: [
      {
        label: t('forms.reviewTitles.beneficiary'),
        value: beneficiaryName.value
      },
      {
        label: t('forms.reviewTitles.iban'),
        value: formatIBAN(props.bankTransferData.iban)
      },
      {
        label: t('forms.reviewTitles.receiving'),
        value: formattedAmount.value,
        error: !greaterThan(amountWithoutFee.value, MIN_SEND_AMOUNT_EUR)
      }
    ]
  }
]);

const additionalDetails = computed((): UiListPairGroup[] => [
  {
    items: [
      {
        label: t('forms.reviewTitles.total'),
        value: formattedTotal.value,
        token: state.otcData.fromToken
      },
      {
        label: t('forms.reviewTitles.network'),
        value: getNetwork(state.otcData.fromToken.network)?.displayedName,
        image: getNetwork(state.otcData.fromToken.network)?.iconURL
      },
      {
        label: t('forms.reviewTitles.rate'),
        value: formattedRate.value
      },
      {
        label: t('forms.reviewTitles.bic'),
        value: props.bankTransferData.bic.toUpperCase()
      },
      {
        label: t('forms.reviewTitles.reference'),
        value: props.reference,
        hidden: !props.reference
      },
      {
        label: t('detailsBeneficiary.reference'),
        value:
          `${typeof accountData.value?.name === 'string' ? accountData.value.name.toUpperCase() : ''} HOLYHELD ${dayjs().format('DDMMYYYY')}`.trim()
      }
    ].filter((item) => !item.hidden)
  }
]);

const aggregateFeesText = computed((): string => {
  return formatAmount(aggregateFees.value, 2, CurrencyCode.EUR, AmountMode.SymbolFirst, false);
});

const isFeeTipVisible = computed(() => {
  return !isZero(aggregateFees.value);
});

const feeModalDetails = computed((): UiListPairGroup[] => {
  return [
    {
      items: [
        {
          label: t('forms.reviewTitles.transactionFee'),
          value: formatAmount(
            state.fees.transactionFee,
            2,
            CurrencyCode.EUR,
            AmountMode.SymbolFirst,
            false
          )
        },
        {
          label: t('forms.reviewTitles.networkFee'),
          value: formatAmount(
            state.fees.networkFee,
            2,
            CurrencyCode.EUR,
            AmountMode.SymbolFirst,
            false
          )
        },
        {
          label: t('forms.reviewTitles.transferFee'),
          value: formatAmount(
            state.fees.transferFee,
            2,
            CurrencyCode.EUR,
            AmountMode.SymbolFirst,
            false
          )
        }
      ]
    }
  ];
});

async function onPressSentTxButton() {
  const { generateQuote } = useOtc();

  loadingTargetAddressAbortController = new AbortController();
  state.isLoading = true;

  try {
    const quote = await generateQuote(
      state.otcData,
      state.fees,
      props.bankTransferData,
      loadingTargetAddressAbortController.signal
    );
    await useTransactions().waitForTransaction(
      quote.quoteId,
      undefined,
      loadingTargetAddressAbortController.signal
    );
    replace({ name: RouteNames.WatchQuote, params: { id: quote.quoteId } });
  } catch (e) {
    if (
      e instanceof APIError &&
      e.code === 'VALIDATION_FAILED' &&
      e.payload !== undefined &&
      typeof e.payload['validationError'] === 'string'
    ) {
      addSentryBreadcrumb({
        level: 'error',
        message: 'Quote validation failed',
        data: {
          error: e,
          validationError: e.payload['validationMessage']
        }
      });

      useErrorModal().notify(e.payload['validationError']);
      return;
    }

    addSentryBreadcrumb({
      level: 'error',
      message: 'fail to load quote',
      data: {
        error: e
      }
    });

    useErrorModal().auto(e);
  } finally {
    state.isLoading = false;
  }
}

function openFeeModal() {
  state.isFeeModalOpened = true;
}

function closeFeeModal() {
  state.isFeeModalOpened = false;
}

const isSendButtonDisabled = computed(() => {
  return !greaterThan(amountWithoutFee.value, MIN_SEND_AMOUNT_EUR);
});
</script>

<template>
  <section class="transaction-review-swap">
    <TransactionReview
      :lottie="bankTransferLottieData"
      :title="t('otc.review.confirmOtc')"
      :details="details"
      :additional-details="additionalDetails"
      :button-text="t('otc.review.requestOtcBtn')"
      :loading="state.isLoading"
      :disabled="isSendButtonDisabled"
      @submit="onPressSentTxButton"
    >
      <FeeInfo
        :value="aggregateFeesText"
        :fee-blinking="state.isLoading"
        :is-tip-visible="isFeeTipVisible"
        @click:tip="openFeeModal"
      />
    </TransactionReview>
    <FeeModal
      :title="t('otc.review.fees')"
      :model-value="state.isFeeModalOpened"
      :details="feeModalDetails"
      @update:model-value="closeFeeModal"
    />
  </section>
</template>

<style scoped>
.transaction-review-swap {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}
</style>
